import { createAdhese, type AdheseSlotContext, type AdheseSlotHooks } from "@adhese/sdk";
import { addTrackingPixel, type Ref } from "@adhese/sdk-shared";

type AdheseConfigInput = {
  account: string;
  location: string;
  consent: boolean;
  data: {
    userID?: string;
    deviceID?: string;
    language?: string;
    categories?: string[];
    currentCategory?: string;
    appVersion?: string;
    searchKeyword?: string;
    loggedIn?: boolean;
  };
  slots: Array<{
    format: string;
    containingElement: string;
    setup?: (context: Ref<AdheseSlotContext | null>, hooks: AdheseSlotHooks) => void;
  }>;
};

declare const window: {
  adhese: ReturnType<typeof createAdhese> & {
    refresh: (newConfig?: AdheseConfigInput) => void;
    trackURL: (url: string) => void;
  };
  AdheseConfig: AdheseConfigInput;
} & Window;

function trackURL(url: string): void {
  addTrackingPixel(url);
}

function getCategoryPrefix(location: AdheseConfigInput["location"]): string {
  return location?.split("_")[0] ?? "";
}

function prefixCategory(prefix: string, category: string): string {
  if (!prefix || category.startsWith(`${prefix}_`)) return category;

  return `${prefix}_${category}`;
}

function toAdheseParams(
  data: AdheseConfigInput["data"],
  consent: AdheseConfigInput["consent"],
  location: AdheseConfigInput["location"],
): Record<string, string[]> {
  const categoryPrefix = getCategoryPrefix(location);
  const params: Record<string, string[]> = {};
  const normalized: Record<string, unknown> = Object.fromEntries(
    Object.entries(data as Record<string, unknown>).map(([k, v]) => [
      k.toLowerCase(),
      v,
    ]),
  );

  if (consent === true && normalized.userid !== undefined)
    params.mi = [normalized.userid as string];
  if (normalized.deviceid !== undefined)
    params.di = [normalized.deviceid as string];
  if (normalized.language !== undefined)
    params.ln = [normalized.language as string];
  if (normalized.categories !== undefined)
    params.ct = (normalized.categories as string[]).map((category) =>
      prefixCategory(categoryPrefix, category),
    );
  if (normalized.currentcategory !== undefined)
    params.cc = [prefixCategory(categoryPrefix, normalized.currentcategory as string)];
  if (normalized.searchkeyword !== undefined)
    params.kw = [normalized.searchkeyword as string];
  if (normalized.appversion !== undefined)
    params.ve = [normalized.appversion as string];
  if (normalized.loggedin !== undefined)
    params.li = [normalized?.loggedin?.toString() as string];

  return params;
}

const initAdhese = (adheseConfig?: AdheseConfigInput) => {
  const config = adheseConfig || window.AdheseConfig;
  const { data, slots, consent, ...rest } = config;

  const adhese = createAdhese({
    ...rest,
    consent,
    parameters: toAdheseParams(data, consent, rest.location),
    initialSlots: slots.map(({ containingElement, ...slot }) => ({
      ...slot,
      containingElement: containingElement,
      setup(context, hooks) {
        slot.setup?.(context, hooks);
        hooks.onRequest((ad) => {
          const title = typeof ad.tag === "string" ? ad.tag.match(/<title>([\s\S]*?)<\/title>/i)?.[1] : undefined;
          if (title === "Empty") {
            context?.value?.processOnEmpty(ad);
            return ad;
          }
        });
      },
    })),
  });

  window.AdheseConfig = config;

  window.adhese = adhese as any;

  window.adhese.trackURL = trackURL;

  window.adhese.refresh = (newConfig?: AdheseConfigInput) => {
    window.adhese.dispose();
    initAdhese(newConfig);
  };
};

initAdhese();
